/* storybook-check-ignore */
import React, { useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box } from '@opendoor/bricks/core';
import { globalObservability } from '@opendoor/observability/slim';
import Cookies from 'js-cookie';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';

import {
  OdProtosCasaData_Result,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { useObservability } from 'helpers/observability';
import useQueryParams from 'helpers/queryParams';

import { AddressSearchLocation, TrackingTaxonomy } from '../../components/globals';
import { AddressConfirmationModal } from '../../components/landing-pages-v2/AddressConfirmationModal';
import NovoAddressInput, { AutoCompletePrediction } from '../../components/novo/NovoAddressInput';
import {
  ADDRESS_INVALID_RESULT,
  inOffMarketPdpExperiment,
  SelectedAddress,
  useAddressValidationContext,
} from '../../helpers/AddressValidationContext';
import {
  ALLOWED_TYPES,
  buildAutocompleteUrl,
  buildPlacesUrl,
  generateGoogleMapsSessionToken,
} from '../../helpers/googleMaps';
import { listUnits, standardizeAddress } from '../api';
import { getAnonymousId } from './trackers';

const DEBOUNCE_INVOKE_WAIT_MS = 100;
const UNIVERSAL_NIL_ADDRESS = '6aae647b-d603-5533-8464-c42c658b101a';

interface NewSellerFlowAddressInputProps {
  inputLocation: AddressSearchLocation;
  trackingTaxonomy: TrackingTaxonomy;
  analyticsPrefix?: string;
  placeholder: string;
  hideLabel?: boolean;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
}

export const gateManualAddressVerification = async ({
  forceRedirect,
  address,
}: {
  forceRedirect: boolean;
  address?: SelectedAddress;
}) => {
  let moreResult: OdProtosCasaData_Result[] = [];
  if (forceRedirect || !address) {
    return { shouldRedirect: true, destination: '/manual-address-entry' };
  }

  let casaAddress = address;
  if (address.source === 'google') {
    const standardizeAddressResponse = await standardizeAddress(address);
    if (
      !standardizeAddressResponse ||
      !standardizeAddressResponse.data ||
      standardizeAddressResponse.errors ||
      standardizeAddressResponse.data?.casa.findByFields?.address?.uuid === UNIVERSAL_NIL_ADDRESS
    ) {
      moreResult = ['ADDRESS_UNKNOWN'];
      return {
        shouldRedirect: true,
        destination: '/manual-address-entry',
        standardizeAddress: address,
        moreResult,
      };
    }
    const addressResult = standardizeAddressResponse.data?.casa.findByFields?.address;
    casaAddress = {
      id: addressResult.uuid,
      street1: addressResult.street,
      city: addressResult.city,
      state: addressResult.state,
      postal_code: addressResult.postalCode,
      unit: addressResult.unitNumber,
      result: addressResult.result,
    };
  }

  // it's possible for an address to be correct with or without a unit number
  // in these cases, casa won't include the ADDRESS_MISSING_SUB_PREMISE flag, so we have to call
  // a separate endpoint to fetch all possible units for that address.
  if (!casaAddress?.result?.includes('ADDRESS_MISSING_SUB_PREMISE') && casaAddress?.unit === '') {
    if (casaAddress.id && (await doesAddressHaveMultipleUnits(casaAddress.id))) {
      moreResult = ['ADDRESS_MISSING_SUB_PREMISE'];
    }
  }

  if (ADDRESS_INVALID_RESULT.some((error) => casaAddress?.result?.includes(error))) {
    return {
      shouldRedirect: true,
      destination: '/manual-address-entry',
      standardizeAddress: casaAddress,
      moreResult,
    };
  }
  return { shouldRedirect: false, standardizeAddress: casaAddress, moreResult };
};

const doesAddressHaveMultipleUnits = async (addressUuid: string) => {
  const response = await listUnits(addressUuid);
  const addresses = response?.data?.casa?.listUnits?.addresses || [];

  return addresses.some(
    (address: any) =>
      address.uuid !== addressUuid &&
      !address.result?.some((casaResult: OdProtosCasaData_Result) =>
        ADDRESS_INVALID_RESULT.includes(casaResult),
      ),
  );
};

const NewSellerFlowAddressInput: React.FC<NewSellerFlowAddressInputProps> = ({
  inputLocation,
  trackingTaxonomy,
  hideLabel,
  placeholder,
  pageSetPath,
  trackingKeywords,
  channel,
}) => {
  const router = useRouter();
  const { trackJourneyEvent } = useObservability();
  const { user } = useAuth();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);

  const {
    isPredictionSelectLoading,
    validatedAddress,
    setValidatedAddress,
    setAddressResults,
    setIsPredictionSelectLoading,
    setFirstPrediction,
    setPartnerReferralId,
    setPageSetPath,
    setTrackingKeywords,
    setChannel,
  } = useAddressValidationContext();
  const [searchTerm, setSearchTerm] = useState('');

  const { partner_referral, pa } = useQueryParams();
  const sessionToken = generateGoogleMapsSessionToken();

  useEffect(() => {
    if (partner_referral) {
      setPartnerReferralId(partner_referral as string);
    }
    if (pageSetPath) {
      setPageSetPath(pageSetPath as string);
    }
    if (pa) {
      setSearchTerm(pa as string);
      fetchAutocompleteOptions(pa as string);
    }
    if (trackingKeywords) {
      setTrackingKeywords(trackingKeywords as string);
    }
    if (channel) {
      setChannel(channel as OdProtosSellReceptionData_SellerInput_Channel);
    }
  }, [partner_referral, pa, pageSetPath, trackingKeywords, channel]);

  const fetchAutocompleteOptions = debounce((input: string) => {
    if (!input) {
      setPredictions([]);
      return;
    }
    fetch(buildAutocompleteUrl(input, sessionToken))
      .then((res) => res.json())
      .then((res) => {
        res.predictions.forEach((result: AutoCompletePrediction) => {
          result.description = result.description.replace(', USA', '');
        });

        const autocompleteOptions = res.predictions.filter((result: AutoCompletePrediction) => {
          result.source = res.source;
          if (res.source === 'google') {
            const terms = result.terms ?? [];
            const resultAllowedTypes = (result.types ?? []).filter((value) =>
              ALLOWED_TYPES.includes(value),
            );
            return (
              (terms.length >= 5 && resultAllowedTypes.length != 0) ||
              (terms.length === 4 && terms[0].value.match(/^\d+\S*\s\S+\s/) !== null)
            );
          }
          return result;
        });
        setPredictions(autocompleteOptions);
        if (autocompleteOptions.length > 0) {
          setFirstPrediction(autocompleteOptions[0]);
        }
      })
      .catch(() => setPredictions([]));
  }, DEBOUNCE_INVOKE_WAIT_MS);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;

    setSearchTerm(address);
    fetchAutocompleteOptions(address);
    if (address === '') setPredictions([]);
  };

  const addressVerification = async (address: SelectedAddress) => {
    const result = await gateManualAddressVerification({
      forceRedirect: false,
      address,
    });
    const combinedResults = [
      ...(result?.standardizeAddress?.result || []),
      ...(result?.moreResult || []),
    ];
    setAddressResults(combinedResults);
    setValidatedAddress(result?.standardizeAddress);
    if (result.shouldRedirect && result.destination) {
      router.push({
        pathname: result.destination,
        query: {
          address: JSON.stringify(result?.standardizeAddress),
          result: combinedResults,
          ...(partner_referral != null && { partnerReferralId: partner_referral as string }),
          ...(pageSetPath != null && { pageSetPath }),
          ...(trackingKeywords != null && { trackingKeywords }),
          ...(channel != null && { channel }),
        },
      });
    } else {
      if (combinedResults?.includes('ADDRESS_MISSING_SUB_PREMISE')) {
        setIsConfirmationModalOpen(true);
        return;
      }
      const localeCookie = Cookies.get('locale');
      const anonId = getAnonymousId();
      const inExperiment = await inOffMarketPdpExperiment(
        user?.customerUuid || '',
        localeCookie,
        anonId,
      );
      if (inExperiment) {
        router.push({
          pathname: `/property-detail/${result?.standardizeAddress?.id}`,
          query: {
            ...(partner_referral != null && { partnerReferralId: partner_referral as string }),
            ...(pageSetPath != null && { pageSetPath }),
            ...(trackingKeywords != null && { trackingKeywords }),
            ...(channel != null && { channel }),
          },
        });
      } else {
        setIsConfirmationModalOpen(true);
      }
    }
  };

  const handlePredictionSelect = async (prediction: AutoCompletePrediction) => {
    setSearchTerm(prediction.description);
    setIsPredictionSelectLoading(true);

    const url =
      prediction.source === 'google'
        ? buildPlacesUrl({
            source: 'google',
            placeId: prediction.place_id,
            sessionToken,
          })
        : buildPlacesUrl({
            source: 'casa',
            addressToken: prediction.addressToken,
            sessionToken,
          });

    try {
      const response = await fetch(url);
      const address: SelectedAddress = await response.json();
      await addressVerification(address);
      trackJourneyEvent('sell', 'addressEntryV2', {
        street1: address.street1,
        city: address.city,
        state: address.state,
        postalCode: address.postal_code,
        unit: address.unit,
        latitude: address.latitude,
        longitude: address.longitude,
        sourcePage: router.asPath,
        campaign: router.query?.utm_campaign as string,
      });
    } catch (e) {
      globalObservability.getSentryClient().captureException?.(e);
    } finally {
      setIsPredictionSelectLoading(false);
    }
  };

  return (
    <Box flex={1}>
      <NovoAddressInput
        id="home-address-input"
        placeholder={placeholder}
        hideLabel={hideLabel}
        disabled={false}
        prediction={{
          predictions: predictions,
          setPredictions: setPredictions,
          searchTerm: searchTerm,
          setSearchTerm: setSearchTerm,
          handlePredictionSelect: handlePredictionSelect,
          onChange: handleAddressChange,
        }}
        product="SELL_DIRECT"
        pageSetPath={pageSetPath}
        trackingKeywords={trackingKeywords}
        partnerReferralId={partner_referral as string}
        channel={channel}
      />
      {isConfirmationModalOpen && !isPredictionSelectLoading && validatedAddress && (
        <AddressConfirmationModal
          setIsOpen={setIsConfirmationModalOpen}
          isOpen={isConfirmationModalOpen}
          partnerReferralId={partner_referral as string}
          initialAddress={validatedAddress}
          pageSetPath={pageSetPath}
          trackingKeywords={trackingKeywords}
          channel={channel}
          inputLocation={inputLocation}
          trackingTaxonomy={trackingTaxonomy}
        />
      )}
    </Box>
  );
};

export default NewSellerFlowAddressInput;
